import { useMemo } from 'react'
import { Box, Divider, Typography } from '@mui/material'
import { useGetDataListEntries } from '@/service-library/hooks/data-list-entries'
import { useGetChildDataList } from '@/service-library/hooks/data-lists'

type EntrataNeedsReviewSectionProps = {
  cardType: 'property' | 'vendor'
  columnId?: string
  orgId?: string
}

const PROPERTIES_TO_STOP_ROOT_DATA_LIST_ID =
  '01981fb0-d9bd-2bdf-a4ec-30eb74e24390'

const VENDORS_TO_STOP_ROOT_DATA_LIST_ID = '01971cde-7d97-b01d-2119-eba2217dd598'

export default function EntrataNeedsReviewSection({
  cardType,
  columnId,
  orgId,
}: EntrataNeedsReviewSectionProps) {
  const { dataList } = useGetChildDataList({
    id:
      cardType === 'property'
        ? PROPERTIES_TO_STOP_ROOT_DATA_LIST_ID
        : VENDORS_TO_STOP_ROOT_DATA_LIST_ID,
    orgId: orgId || '',
    filters: { fields__include: 'parent_list_columns,data_list_columns' },
    enabled: !!orgId,
  })

  const filters = useMemo(() => {
    const baseFilters: Record<string, string | undefined> = {
      limit: '1',
      data_list_id: dataList?.id,
      fields__only: 'id,data_list_entry_cells',
      data_list_entry_cells__fields__only:
        'id,data_list_entry_cell_values,data_list_column_id',
      data_list_entry_cells__data_list_entry_cell_values__fields__only:
        'id,value',
    }
    if (cardType === 'vendor') {
      baseFilters['column__Id__icontains'] = columnId
    } else {
      baseFilters['column__Entrata ID__icontains'] = columnId
    }
    return baseFilters
  }, [cardType, columnId, dataList?.id])

  const { dataListEntries: reasonToStopEntries } = useGetDataListEntries({
    filters,
    enabled: !!dataList && !!columnId,
  })

  const reasonValue = useMemo(() => {
    if (!reasonToStopEntries?.length) return ''
    const reasonColumnId = (
      dataList?.parent_list_columns || dataList?.data_list_columns
    )?.find(({ name }) => name === 'Reason')?.id

    if (!reasonColumnId) return ''
    const entry = reasonToStopEntries[0]
    const cell = entry.data_list_entry_cells.find(
      (cell) => cell.data_list_column_id === reasonColumnId,
    )
    return cell?.data_list_entry_cell_values[0]?.value || ''
  }, [
    reasonToStopEntries,
    dataList?.data_list_columns,
    dataList?.parent_list_columns,
  ])

  if (!reasonToStopEntries.length) return null
  return (
    <Box sx={{ pt: 1 }}>
      <Typography variant="body2" color="text.secondary">
        {cardType === 'property' ? 'Property' : 'Vendor'} needs review:{' '}
        <Typography
          variant="inherit"
          component="span"
          color={reasonValue ? 'neutral' : undefined}
          sx={{
            fontStyle: reasonValue ? 'normal' : 'italic',
          }}
        >
          {reasonValue || 'No reason provided.'}
        </Typography>
      </Typography>
      <Divider sx={{ mt: 1 }} />
    </Box>
  )
}
